import React, { useState, useMemo } from "react";
import { useFormikContext } from "formik";
import AutoCompleteFibre from "./AutoCompleteFibre";
import GarmentFibreComponent from "./GarmentFibreComponent";
import { checkGarmentpercentage } from "../../CommonFuntion/SchemaValidation";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Delete } from "@mui/icons-material";

const GarmentComponent = ({
  name,
  garmentIndex,
  garment,
  setOpen,
  headerList,
  options,
  showAlias,
  mandatoryGarmentPart,
  componentMinMax,
  getTotalParts
}) => {
  const { values, setFieldValue } = useFormikContext();
  const [hiddenFields, setHiddenFields] = useState({});

  const handleAutoCompleteChange = (garmentIndex, fibreIndex, shouldHide) => {
    setHiddenFields((prevState) => ({
      ...prevState,
      [`${garmentIndex}-${fibreIndex}`]: shouldHide,
    }));
  };

  const maxComponents = useMemo(() => {
    if (!componentMinMax) return Infinity;
    const parts = String(componentMinMax).split("/");
    const x = parseInt(parts[1], 10);
    return isNaN(x) ? Infinity : x;
  }, [componentMinMax]);

  const openMessage = (message, isValid) =>
    setOpen({ operBar: true, message, isValidPercentage: isValid });

  const openLimitExceeded = () =>
    openMessage(`You cannot enter more than ${maxComponents} components`, false);

  const addGarment = (index) => {
    const newData = [...values[name]];
    newData.splice(index + 1, 0, {
      garment: [{ garment: "" }],
      percentage: 0,
      isSave: false,
      isEdit: false,
      fibres: [{ fibre: "", percentage: 0 }],
    });
    setFieldValue(name, newData);
  };
  const addGarmentPart = (garmentPartIndex, garmentPart) => {
    if (getTotalParts() >= maxComponents) {
      openLimitExceeded();
      return;
    }
    garmentPart.garment.splice(garmentPartIndex + 1, 0, { garment: "" });
  };

  const removeGarmentPart = (garmentPartIndex, garmentPart) => {
    garmentPart.garment.splice(garmentPartIndex, 1);
  };
  const removeGarment = (index) => {
    const newData = [...values[name]];
    newData.splice(index, 1);
    setFieldValue(name, newData);
  };

  const saveGarment = () => {
    const [garmentPercentage, isValidPercentage, message] =
      checkGarmentpercentage(garment, garmentIndex, mandatoryGarmentPart);
    garment.percentage = garmentPercentage.toString();
    const exceedsMax = getTotalParts() >= maxComponents;

    if (isValidPercentage && garment.garment) {
      garment.isEdit = false; // ensure it is counted after save

      // prevent adding a new garment when at or over the max
      if (!garment.isEdit && !exceedsMax) {
        addGarment(garmentIndex);
      } else if (exceedsMax) {
        openLimitExceeded();
      }
      garment.errorBorder = "";
    } else {
      garment.errorBorder = "error-border";
    }

    if (getTotalParts() > maxComponents) {
      openLimitExceeded();
      garment.isSave = false;
    } else {
      garment.isSave = !!isValidPercentage;
      openMessage(message, isValidPercentage);
    }

    setFieldValue(`${name}[${garmentIndex}]`, garment);
  };

  const editGarment = () => {
    garment.isSave = false;
    garment.isEdit = true;
    setFieldValue(`${name}[${garmentIndex}]`, garment);
  };

  return (
    <>
      {garment?.garment?.map((garmentPart, garmentPartIndex) => (
        <div key={garmentPartIndex}>
          <div className="fiber-flex-container garment-component">
            <div className="flex-fibre-item-right">
              <AutoCompleteFibre
                options={headerList}
                name={`${name}[${garmentIndex}].garment[${garmentPartIndex}].garment`}
                setFieldValue={setFieldValue}
                garmentFibre={garmentPart}
                disabled={garment?.isSave}
                errorBorder={garment?.errorBorder}
                showAlias={showAlias}
                fullWidth
              />

              {garment?.garment?.length === garmentPartIndex + 1 && true ? (
                <IconButton
                  disabled={garment?.isSave}
                  onClick={() => addGarmentPart(garmentPartIndex, garment)}
                >
                  <AddIcon fontSize="small" color="primary" />
                </IconButton>
              ) : null}

              {garment?.garment?.length !== 1 ? (
                <IconButton
                  disabled={garment?.isSave}
                  onClick={() => removeGarmentPart(garmentPartIndex, garment)}
                >
                  <RemoveIcon color="primary" />
                </IconButton>
              ) : null}
            </div>

            <div className="flex-fibre-item-right">
              <label name={`${name}[${garmentIndex}].percentage`}>
                {garment?.percentage}%
              </label>

              {garmentPartIndex === 0 ? (
                garment?.isSave ? (
                  <IconButton onClick={editGarment}>
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                ) : (
                  <IconButton onClick={saveGarment}>
                    <SaveIcon fontSize="small" color="primary" />
                  </IconButton>
                )
              ) : null}

              {values[name]?.length - 1 !== garmentIndex &&
              garmentPartIndex === 0 ? (
                <IconButton
                  disabled={garment?.isSave}
                  onClick={() => removeGarment(garmentIndex)}
                >
                  <Delete color="primary" />
                </IconButton>
              ) : null}
            </div>
          </div>
        </div>
      ))}

      {garment?.fibres?.map((fibre, fibreIndex) => (
        <GarmentFibreComponent
          key={fibreIndex}
          name={name}
          fibre={fibre}
          garmentIndex={garmentIndex}
          fibreIndex={fibreIndex}
          garment={garment}
          options={options}
          showAlias={showAlias}
          isFieldHidden={hiddenFields[`${garmentIndex}-${fibreIndex}`]}
          onAutoCompleteChange={(shouldHide) =>
            handleAutoCompleteChange(garmentIndex, fibreIndex, shouldHide)
          }
          maxComponents={maxComponents}
          setOpen={setOpen}
          globalTotalParts={getTotalParts()}
        />
      ))}
    </>
  );
};

export default React.memo(GarmentComponent);
